import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { authUtils } from '../utils/auth';

export interface MessageHistoryItem {
  id: string;
  role: string;
  content: string;
  timestamp: string;
  metadata?: any;
}

export interface ConversationHistoryItem {
  session_id: string;
  first_message: string;
  last_message: string;
  messages: MessageHistoryItem[];
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface ConversationHistoryResponse {
  conversations: ConversationHistoryItem[];
  total: number;
  limit: number;
}

const STORAGE_KEY = 'conversation_history';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

class HistoryService {
  private lastFetchTime: number = 0;

  async loadConversationHistory(forceRefresh = false): Promise<ConversationHistoryItem[]> {
    // Check session storage first
    if (!forceRefresh) {
      const cached = this.getCachedHistory();
      if (cached) {
        return cached;
      }
    }

    try {
      const token = authUtils.getToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await axios.get<ConversationHistoryResponse>(
        API_ENDPOINTS.CHAT.CONVERSATIONS,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 10000,
        }
      );

      if (response.data && response.data.conversations) {
        // Filter out conversations with no messages
        const filteredConversations = response.data.conversations.filter(
          (conv: ConversationHistoryItem) => conv.messages && conv.messages.length > 0
        );
        this.setCachedHistory(filteredConversations);
        return filteredConversations;
      }

      return [];
    } catch (error) {
      console.error('Failed to load conversation history:', error);
      // Return cached data if available on error
      const cached = this.getCachedHistory();
      return cached || [];
    }
  }

  private getCachedHistory(): ConversationHistoryItem[] | null {
    const now = Date.now();
    if (now - this.lastFetchTime > CACHE_DURATION) {
      return null;
    }

    const cached = sessionStorage.getItem(STORAGE_KEY);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        return null;
      }
    }
    return null;
  }

  private setCachedHistory(conversations: ConversationHistoryItem[]): void {
    this.lastFetchTime = Date.now();
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
  }

  clearCache(): void {
    sessionStorage.removeItem(STORAGE_KEY);
    this.lastFetchTime = 0;
  }

  getConversationById(sessionId: string): ConversationHistoryItem | null {
    const cached = this.getCachedHistory();
    if (!cached) return null;

    return cached.find(conv => conv.session_id === sessionId) || null;
  }

  /**
   * Optimistically add or update a conversation in the local cache
   * This allows immediate UI updates without waiting for backend
   */
  addOrUpdateConversationLocally(conversation: ConversationHistoryItem): void {
    // Don't add conversations with no messages (empty chats)
    if (conversation.messages.length === 0 || conversation.message_count === 0) {
      console.log('Skipping empty conversation from history');
      return;
    }

    const cached = this.getCachedHistory() || [];

    // Check if conversation already exists
    const existingIndex = cached.findIndex(conv => conv.session_id === conversation.session_id);

    if (existingIndex >= 0) {
      // Update existing conversation
      cached[existingIndex] = conversation;
    } else {
      // Add new conversation at the beginning (most recent first)
      cached.unshift(conversation);
    }

    this.setCachedHistory(cached);
  }

  /**
   * Create a conversation history item from current chat state
   */
  createConversationFromMessages(
    sessionId: string,
    messages: Array<{ id: string; role: string; content: string; timestamp: Date; isWelcome?: boolean }>
  ): ConversationHistoryItem {
    // Filter out welcome messages
    const nonWelcomeMessages = messages.filter(msg => !msg.isWelcome);
    const userMessages = nonWelcomeMessages.filter(msg => msg.role === 'user');

    const firstUserMessage = userMessages[0];
    const lastUserMessage = userMessages[userMessages.length - 1];

    return {
      session_id: sessionId,
      first_message: firstUserMessage?.content || 'New conversation',
      last_message: lastUserMessage?.content || firstUserMessage?.content || 'New conversation',
      messages: nonWelcomeMessages.map(msg => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp.toISOString(),
      })),
      created_at: messages[0]?.timestamp.toISOString() || new Date().toISOString(),
      updated_at: messages[messages.length - 1]?.timestamp.toISOString() || new Date().toISOString(),
      message_count: nonWelcomeMessages.length,
    };
  }
}

export const historyService = new HistoryService();